import React from "react";
import axios from "axios";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import ButtonComponent from "../components/ui/ButtonComponent";
import TextInputComponent from "../components/ui/TextInputComponent";
import SelectInputComponent from "../components/ui/SelectInputComponent";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Paper,
  Divider,
  Switch,
  Slider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useToast } from "../components/ui/ToastContext";
import FormSkeleton from "../components/ui/FormSkeleton";

import { auth } from "../firebase-config";      // staff session
import { authPortal } from "../firebase-portal"; // client session
import { useActAsClient } from "./portal/ActAsClientContext";

const GET_FORM_DEF_URL =
  process.env.REACT_APP_GET_FORM_DEFINITION_URL ||
  "https://getformdefinition-774073159618.us-central1.run.app";
const GENERATE_PDF_URL =
  process.env.REACT_APP_GENERATE_FORM_PDF_URL ||
  "https://generateformpdf-774073159618.us-central1.run.app";
const SUBMIT_MINDBODY_URL =
  process.env.REACT_APP_SUBMIT_TO_MINDBODY_URL ||
  "https://submittomindbody-774073159618.us-central1.run.app";
const GET_ASSIGNED_FORMS_URL =
  process.env.REACT_APP_GET_ASSIGNED_FORMS_URL ||
  "https://getclientforms-774073159618.us-central1.run.app";

/* helpers unchanged */
const fieldDefaultValue = (field) => {
  if (Object.prototype.hasOwnProperty.call(field, "default")) return field.default;
  switch (field.type) {
    case "multiselect":
      return [];
    case "checkbox":
    case "switch":
      return false;
    case "slider":
      return field.min ?? 0;
    case "radio":
    case "number":
    case "email":
    case "textarea":
    case "select":
    case "text":
      return "";
    case "date":
      return null;
    default:
      return "";
  }
};
const toLabelCase = (str = "") =>
  String(str)
    .replace(/[_\-]+/g, " ")
    .replace(/([a-z09])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
const getFieldLabel = (field) =>
  field.label?.trim() || (field.name ? toLabelCase(field.name) : toLabelCase(field.id));
const normalizeValue = (field, raw) => {
  if (raw == null) return "";
  switch (field.type) {
    case "checkbox":
    case "switch":
      return raw ? "Yes" : "No";
    case "multiselect":
      return Array.isArray(raw) ? raw.join(", ") : String(raw);
    case "date":
      return typeof raw === "string" ? raw : "";
    case "slider":
    case "number":
      return raw === "" ? "" : String(raw);
    default:
      return Array.isArray(raw) ? raw.join(", ") : String(raw);
  }
};
const buildDisplayData = (schema, values) => {
  const out = {};
  (schema?.fields || []).forEach((f) => {
    if (f.type === "section" || f.type === "divider") return;
    const label = getFieldLabel(f);
    out[label] = normalizeValue(f, values[f.id]);
  });
  return out;
};
const formatFormDataForNotes = (data, schema) => {
  if (!data || !schema || !schema.noteSections) return "";
  const lines = [];
  const fieldMap = new Map(schema.fields.map((f) => [f.name, f]));
  for (const section of schema.noteSections) {
    if (section.title && Array.isArray(section.fieldNames)) {
      lines.push(`${section.title}:`);
      for (const fieldName of section.fieldNames) {
        const field = fieldMap.get(fieldName);
        if (field) {
          const value = data[field.id] || "N/A";
          lines.push(`  - ${field.label}: ${value}`);
        }
      }
      lines.push("");
    }
  }
  return lines.join("\n");
};

export default function FormFillerPortal() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { addToast } = useToast();

  const actAs = useActAsClient();

  const assignmentIdFromPortal = location.state?.assignmentId || null;
  const assignmentOwnerClientId = location.state?.assignmentClientId || null;

  const [portalClientInfo, setPortalClientInfo] = React.useState(null);
  const [portalComplete, setPortalComplete] = React.useState(false);
  const [afterSubmitLoadingNext, setAfterSubmitLoadingNext] = React.useState(false);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [formMeta, setFormMeta] = React.useState(null);
  const [version, setVersion] = React.useState(null);
  const [values, setValues] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [pdfUrl, setPdfUrl] = React.useState(null);

  const getAuthContext = React.useCallback(async () => {
    const isActingAs = !!actAs?.isActingAs;
    const currentUser = isActingAs ? auth.currentUser : authPortal.currentUser;

    if (!currentUser) {
      throw new Error(isActingAs ? "No staff user" : "You're not signed in to the client portal.");
    }

    const idToken = await currentUser.getIdToken(true);
    const actAsHeaders = isActingAs ? actAs.getActAsHeaders() : {};
    const headers = { Authorization: `Bearer ${idToken}`, ...actAsHeaders };

    let orgId = null;
    let clientId = null;

    if (isActingAs) {
      orgId = actAs?.orgId || null;
      clientId = actAs?.clientId || null;
    } else {
      const tokenResult = await currentUser.getIdTokenResult(true);
      const claims = tokenResult?.claims || {};
      orgId = claims.organizationId || claims.orgId || null;
      clientId = claims.clientId || claims.client_id || claims.portalClientId || null;
    }

    return { isActingAs, currentUser, idToken, headers, orgId, clientId };
  }, [actAs]);

  React.useEffect(() => {
    (async () => {
      try {
        const { orgId, clientId } = await getAuthContext();
        setPortalClientInfo({ orgId, clientId });
      } catch (err) {
        console.error("[FormFillerPortal] auth context failed:", err);
      }
    })();
  }, [getAuthContext]);

  React.useEffect(() => {
    let mounted = true;

    const isExpectingActAs = params.has("actAsClient") || params.has("as");
    if (isExpectingActAs && !actAs.isActingAs) return;

    const initFromDefinition = (def) => {
      const fields = def?.schema?.fields || [];
      const initVals = {};
      fields.forEach((f) => (initVals[f.id] = fieldDefaultValue(f)));
      setFormMeta({ id: def.id, name: def.name, description: def.description });
      setVersion({ id: def.currentVersion, schema: def.schema });
      setValues(initVals);
    };

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        const { headers } = await getAuthContext();

        const { data } = await axios.post(GET_FORM_DEF_URL, { formId }, { headers });

        if (!mounted) return;
        const def = data?.form;
        if (!def) throw new Error("Form definition not found.");
        if (def.status !== "published") {
          setError("This form is not currently published.");
          setLoading(false);
          return;
        }
        initFromDefinition(def);
      } catch (e) {
        if (!mounted) return;
        console.error("[FormFillerPortal] load form failed:", e);
        setError(e?.response?.data?.error || e?.message || "Failed to load form.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [actAs.isActingAs, params, formId, getAuthContext]);

  const isDisqualified = React.useMemo(() => {
    if (!version?.schema?.disqualifyingAnswers) return false;
    const { fields, disqualifyingAnswers } = version.schema;
    const fieldMap = new Map(fields.map((f) => [f.name, f.id]));
    return Object.entries(disqualifyingAnswers).some(([fieldName, disq]) => {
      const fieldId = fieldMap.get(fieldName);
      return fieldId && values[fieldId] === disq;
    });
  }, [version, values]);

  const handleChange = (field, nextVal) => {
    setValues((prev) => ({ ...prev, [field.id]: nextVal }));
  };

  const validate = () => {
    const errs = {};
    (version?.schema?.fields || []).forEach((f) => {
      const v = values[f.id];
      if (f.required) {
        const empty =
          (Array.isArray(v) && v.length === 0) ||
          v === "" ||
          v === null ||
          v === undefined ||
          (typeof v === "boolean" && v === false && f.type !== "checkbox");
        if (empty) errs[f.id] = `${f.label || f.id} is required`;
      }
    });
    return errs;
  };

  const handleAfterPortalSubmit = React.useCallback(async () => {
    try {
      setAfterSubmitLoadingNext(true);

      const { headers } = await getAuthContext();

      const { data } = await axios.post(GET_ASSIGNED_FORMS_URL, {}, { headers });
      const remaining = Array.isArray(data?.forms) ? data.forms : [];
      const filtered = remaining.filter(
        (f) =>
          f.id !== formId &&
          f.assignmentId !== assignmentIdFromPortal &&
          f.assignment_id !== assignmentIdFromPortal
      );

      if (filtered.length === 0) {
        setPortalComplete(true);
        return;
      }

      const nextForm = filtered[0];
      navigate(`/portal/forms/fill/${nextForm.id}`, {
        replace: true,
        state: {
          clientMode: true,
          assignmentId: nextForm.assignmentId || nextForm.assignment_id || null,
          assignmentClientId: nextForm.clientId || null,
        },
      });
    } catch (err) {
      console.error("[FormFillerPortal] next task error:", err);
      setPortalComplete(true);
    } finally {
      setAfterSubmitLoadingNext(false);
    }
  }, [getAuthContext, formId, assignmentIdFromPortal, navigate]);

  const onSubmit = async () => {
    const vErrs = validate();
    setErrors(vErrs);
    if (Object.keys(vErrs).length > 0) {
      addToast("Please complete required fields.", "warning");
      return;
    }

    if (isDisqualified) {
      const confirmProceed = window.confirm(
        "You appear to be disqualified based on your answers.\n\nSubmit anyway?"
      );
      if (!confirmProceed) return;
    }

    setSubmitting(true);
    try {
      const { headers, orgId, clientId } = await getAuthContext();

      const dataByName = Object.fromEntries(
        (version.schema.fields || []).map((f) => [f.name, values[f.id] || ""])
      );

      const clientFullName =
        dataByName.full_name || dataByName.fullName || dataByName.client_name || "";
      const clientEmail =
        dataByName.email_address || dataByName.email || dataByName.client_email || "";
      const clientPhone =
        dataByName.phone_number || dataByName.phone || dataByName.client_phone || "";

      const displayData = buildDisplayData(version.schema, values);
      const displayDataWithClient = {
        ...displayData,
        "Full Name": clientFullName,
        Email: clientEmail,
        Phone: clientPhone,
      };

      const pdfResp = await axios.post(
        GENERATE_PDF_URL,
        {
          formId: formMeta.id,
          formData: values,
          headerText: formMeta.name,
          schema: version.schema,
          displayData,
          displayDataWithClient,
          labelMap: Object.fromEntries(
            (version.schema.fields || [])
              .filter((f) => f.type !== "section" && f.type !== "divider")
              .map((f) => [f.id, getFieldLabel(f)])
          ),
          fullName: clientFullName,
          email: clientEmail,
          phone: clientPhone,
          clientData: { fullName: clientFullName, email: clientEmail, phone: clientPhone },
          orgId: orgId || undefined,
          clientId: clientId || undefined,
          assignmentId: assignmentIdFromPortal || undefined,
          assignmentOwnerClientId: assignmentOwnerClientId || undefined,
          submittedFromPortal: true,
        },
        { headers }
      );

      if (!pdfResp.data?.downloadUrl || !pdfResp.data?.path) {
        throw new Error(pdfResp.data?.message || "Failed to generate PDF.");
      }

      setPdfUrl(pdfResp.data.downloadUrl);
      addToast("Submitted! PDF is ready.", "success");

      await axios.post(
        SUBMIT_MINDBODY_URL,
        {
          ...dataByName,
          fullName: clientFullName,
          email: clientEmail,
          phone: clientPhone,
          pdfPath: pdfResp.data.path,
          formattedNotes: formatFormDataForNotes(values, version.schema),
          clientId: clientId || undefined,
          orgId: orgId || undefined,
          assignmentId: assignmentIdFromPortal || undefined,
          submittedFromPortal: true,
        },
        { headers }
      );

      await handleAfterPortalSubmit();
    } catch (e) {
      console.error("Portal submit failed:", e);
      addToast(e.response?.data?.error || e.message || "Failed to submit form.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderRadio = (field) => {
    const currentValue = values[field.id] ?? "";
    const infoOnValue = field.infoOnValue || null;
    const showInfo = infoOnValue && currentValue === infoOnValue.value;
  
    const options = Array.isArray(field.options) ? field.options : [];
  
    return (
      <Box key={field.id} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {field.label || field.id} {field.required ? "*" : ""}
        </Typography>
  
        <RadioGroup
          row={!!field.inline}
          value={currentValue}
          onChange={(e) => handleChange(field, e.target.value)}
        >
          {options.map((opt, idx) => (
            <FormControlLabel
              key={`${field.id}_opt_${idx}`}
              value={opt.value}
              control={<Radio />}
              label={opt.label}
            />
          ))}
        </RadioGroup>
  
        {showInfo && (
          <Box
            mt={1}
            p={2}
            bgcolor="#fff4e5"
            border="1px solid #ffc107"
            borderRadius={2}
          >
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {infoOnValue.text}
            </Typography>
          </Box>
        )}
  
        {(errors[field.id] || field.helperText) && (
          <Typography
            variant="caption"
            color={errors[field.id] ? "error" : "text.secondary"}
          >
            {errors[field.id] || field.helperText}
          </Typography>
        )}
      </Box>
    );
  };

  const renderField = (field) => {
    const common = {
      key: field.id,
      label: field.label || field.id,
      name: field.id,
      value: values[field.id],
      required: !!field.required,
      error: !!errors[field.id],
      helperText: errors[field.id] || field.helperText || "",
      placeholder: field.placeholder || "",
    };
    switch (field.type) {
      case "divider":
        return (
          <Divider key={field.id} sx={{ my: 3 }}>
            <Typography variant="subtitle2" sx={{ px: 1 }}>
              {field.label}
            </Typography>
          </Divider>
        );
      case "switch":
        return (
          <FormControlLabel
            key={field.id}
            sx={{ justifyContent: "space-between", ml: 0.5, py: 1 }}
            labelPlacement="start"
            label={
              <Typography>
                {field.label || field.id} {field.required ? "*" : ""}
              </Typography>
            }
            control={
              <Switch
                checked={!!values[field.id]}
                onChange={(e) => handleChange(field, e.target.checked)}
              />
            }
          />
        );
      case "slider":
        return (
          <Box key={field.id} sx={{ px: 1, pt: 1 }}>
            <Typography gutterBottom>
              {field.label || field.id} {field.required ? "*" : ""}
            </Typography>
            <Slider
              value={
                typeof values[field.id] === "number"
                  ? values[field.id]
                  : field.min ?? 0
              }
              onChange={(e, v) => handleChange(field, v)}
              valueLabelDisplay="auto"
              min={field.min ?? 0}
              max={field.max ?? 100}
              step={field.step ?? 1}
            />
          </Box>
        );
      case "textarea":
        return (
          <TextInputComponent
            {...common}
            multiline
            onChange={(e) => handleChange(field, e.target.value)}
          />
        );
      case "number":
        return (
          <TextInputComponent
            {...common}
            type="number"
            onChange={(e) => handleChange(field, e.target.value)}
            inputProps={{
              min:
                typeof field.min === "number" ? field.min : undefined,
              max:
                typeof field.max === "number" ? field.max : undefined,
            }}
          />
        );
      case "email":
        return (
          <TextInputComponent
            {...common}
            type="email"
            onChange={(e) => handleChange(field, e.target.value)}
          />
        );
      case "date": {
        const dateValue = values[field.id]
          ? new Date(values[field.id])
          : null;
        const pickerEl = (
          <DatePicker
            key={field.id}
            label={field.label || field.id}
            value={dateValue}
            onChange={(newDate) =>
              handleChange(
                field,
                newDate ? newDate.toISOString().split("T")[0] : null
              )
            }
            renderInput={(params) => (
              <TextInputComponent
                {...params}
                onKeyDown={(e) => e.preventDefault()}
                required={!!field.required}
                error={!!errors[field.id]}
              />
            )}
          />
        );
        if (field.helperText) {
          return (
            <Tooltip
              key={field.id}
              title={field.helperText}
              placement="top-start"
              arrow
            >
              {pickerEl}
            </Tooltip>
          );
        }
        return pickerEl;
      }
      case "select":
        return (
          <SelectInputComponent
            key={field.id}
            value={values[field.id] || ""}
            onChange={(e) => handleChange(field, e.target.value)}
            options={field.options || []}
            required={!!field.required}
            error={!!errors[field.id]}
            label={field.label || field.id}
            multiple={false}
          />
        );
      case "multiselect":
        return (
          <SelectInputComponent
            key={field.id}
            value={values[field.id] || []}
            onChange={(e) => handleChange(field, e.target.value)}
            options={field.options || []}
            required={!!field.required}
            error={!!errors[field.id]}
            label={field.label || field.id}
            multiple
          />
        );
      case "radio":
        return renderRadio(field);
      case "checkbox":
        return (
          <Box key={field.id} sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!values[field.id]}
                  onChange={(e) =>
                    handleChange(field, e.target.checked)
                  }
                />
              }
              label={field.label || field.id}
            />
            {(errors[field.id] || field.helperText) && (
              <Typography
                variant="caption"
                color={errors[field.id] ? "error" : "text.secondary"}
                sx={{ display: "block", mt: -1 }}
              >
                {errors[field.id] || field.helperText}
              </Typography>
            )}
          </Box>
        );
      default:
        return (
          <TextInputComponent
            {...common}
            onChange={(e) => handleChange(field, e.target.value)}
          />
        );
    }
  };

  const groupedSections = React.useMemo(() => {
    if (!version?.schema?.fields) return [];
    const sections = [];
    let currentSection = {
      title: formMeta?.name || "Form",
      description:
        formMeta?.description || "Please complete the fields below.",
      fields: [],
    };
    sections.push(currentSection);
    for (const field of version.schema.fields) {
      if (field.type === "section") {
        currentSection = {
          title: field.title,
          description: field.subtitle,
          fields: [],
        };
        sections.push(currentSection);
      } else {
        currentSection.fields.push(field);
      }
    }
    return sections;
  }, [version, formMeta]);

  const renderPortalEmbedded = () => {
    if (loading) return <FormSkeleton />;
    if (error) return <Alert severity="error">{error}</Alert>;

    if (portalComplete) {
      return (
        <Box
          sx={{
            maxWidth: 420,
            width: "100%",
            bgcolor: "#fff",
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            p: 3,
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            You're all caught up 🎉
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 2 }}
          >
            You’ve completed all assigned forms.
          </Typography>
          <ButtonComponent
            label="Back to portal"
            type="primary"
            onClick={() => navigate("/portal")}
            disabled={afterSubmitLoadingNext}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          mx: "auto",
          p: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: 18, color: "#111827" }}
          >
            {formMeta?.name || "Client Form"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
            Secure submission • Please answer honestly
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            p: { xs: 2, md: 3 },
          }}
        >
          {groupedSections.length === 0 ? (
            <Alert severity="info">This form has no fields.</Alert>
          ) : (
            groupedSections.map((section, index) => (
              <Paper key={index} elevation={2} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {section.title}
                </Typography>
                {section.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {section.description}
                  </Typography>
                )}
                <Box sx={{ display: "grid", gap: 2.5 }}>
                  {section.fields.map((f) => renderField(f))}
                </Box>
              </Paper>
            ))
          )}

          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            <ButtonComponent
              label={submitting ? "Submitting..." : "Submit"}
              type="primary"
              onClick={onSubmit}
              isLoading={submitting}
              disabled={submitting}
            />
            <ButtonComponent
              label="Back to Forms To Do"
              type="secondary"
              onClick={() => navigate("/portal/forms")}
              disabled={submitting}
            />

            {submitting && afterSubmitLoadingNext && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: 1,
                  fontSize: 14,
                  color: "#6B7280",
                }}
              >
                <CircularProgress size={16} />
                <span>Submitting…</span>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {renderPortalEmbedded()}
    </LocalizationProvider>
  );
}